import React from "react";
import SaveWithMetaData from "../../src/taskpane/components/directoryComponents/sharepoint/SaveToSharepoint";
import { useDispatch, useSelector } from "react-redux";
import { SharePointInputData } from "../../src/taskpane/interfaces/props/SharePointInputData";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { Dropdown, Icon, IPanelStyles, IStackItemStyles, IStackStyles, IStackTokens, ITooltipHostStyles, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, ResponsiveMode, Separator, Spinner, SpinnerSize, Stack, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { Slide, ToastContainer } from "react-toastify";
import { DatasourceValidationHelper } from "../../src/taskpane/components/helpers/datasourceValidationHelper";
import { setDatasourceValues } from "../../src/taskpane/redux/modules/datasource";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { IFileToUpload } from "../../src/taskpane/interfaces/IFileToUpload";
import generalModule from "../../utilities/GeneralModule";
import { FolderView } from "../../src/taskpane/components/helpers/folderView";
import { isEmailFormatItem, willSaveAsPdf } from "../../src/taskpane/components/helpers/stringHelper";
import { useSaveFormat } from "../../src/taskpane/components/_v2/redux/hooks/useSaveFormat";
import { SaveFormatChoice } from "../../src/taskpane/components/inputs/SaveFormatChoice";
import { logger } from "../../utilities/logger";
import useDidMountEffect from "../../utilities/Hooks/useDidMountEffect";
import { FileOption } from "../../src/taskpane/interfaces/IFileOption";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { VersionSelectorMinMajOnly } from "../../src/taskpane/components/_v2/_shared/VersionSelector/VersionSelectorMinMajOnly";
import { CheckinTypes } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import DuplicateFiles from "../../src/taskpane/components/directoryComponents/oneDrive/DuplicateFiles";
import { DragAndDropHelper } from "../../utilities/DragAndDropHelper";
import { sharePointInternalFileName, sharePointInternalTitleName } from "../../src/taskpane/config/constants";
import { getSelectedExchangeItemsAsync, resolveEmailMappedInputValue } from "../../src/taskpane/components/helpers/emailMappedFileValues";
import { IFilePreCommitResult } from "../../src/taskpane/interfaces/IFilePreCommitResult";
import authModule from "../../utilities/AuthModule";
import { getFilePreCommitInfo } from "../../src/taskpane/services/SharePointRestServices";
import { updateFileCheckResult } from "../../src/taskpane/redux/modules/file";
import { SaveMode } from "../../src/taskpane/components/enums/SaveModeEnum";
import { ISaveButtonProps } from "../../src/taskpane/interfaces/ISaveButtonProps";
import { getPlatform } from "../../src/taskpane/styles/mobileStyles";
import { refrehOptionsRequest } from "../../src/taskpane/config/datasourceConstants";
import { DatasourceHelper } from "../../src/taskpane/components/helpers/dataSourceHelper";
import { useClientTypeContext } from "../../src/taskpane/components/hooks/useClientTypeContext";
import { resolveDestinationUrl } from "../../src/taskpane/components/helpers/urlDestination";

const containerStackTokens: IStackTokens = {
  childrenGap: 5
};

const containerStackStyles: IStackStyles = {
  root: {
    height: "100%",
  }
};

const metadataStackItemStyles: IStackItemStyles = {
  root: {
    overflowY: "auto"
  }
};

const tooltipHostCalloutProps = { gapSpace: 7 };

const stackTokens: IStackTokens = {
  childrenGap: 8
};

const fileOptionStackStyles: IStackStyles = {
  root: {
    minWidth: 0
  }
}

const fileTypeIconStyles: React.CSSProperties = {
  flexShrink: 0
}

const fileNameStyles: ITooltipHostStyles = {
  root: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    msTextOverflow: "ellipsis"
  }
}

const dropdownStyles: React.CSSProperties = {
  marginTop: "10px",
  paddingLeft: "10px",
  paddingRight: "10px",
  marginBottom: "-10px"
};

export interface ICMMultipleFilesMetaDataPanelProps {
  closePanel?: () => void;
  panelIsOpen: boolean;
  originalInputValues: SharePointInputData[];
  saveFiles?: (savemode: SaveMode, selectedFile?: FileOption, optionsSelected?: boolean, autoRenameDuplicateFolder?: boolean) => void;
  fileOptions: FileOption[];
  setFileOptions?: (fileOptions: any[]) => void;
  checkForDuplicates: boolean;
  setCheckForDuplicates?: (checkForDuplicates: boolean) => void;
  showSelectVersion?: boolean;
  spHasDuplicateFiles?: boolean;
  setSelectedCheckinType?: (checkinType: CheckinTypes) => void;
  onCancel?: (selectedFileOption: FileOption) => void;
}

export const CMMultipleFilesMetaDataPanel = ({ closePanel, panelIsOpen, originalInputValues, fileOptions, setFileOptions, saveFiles, checkForDuplicates, setCheckForDuplicates, showSelectVersion, spHasDuplicateFiles, setSelectedCheckinType, onCancel }: ICMMultipleFilesMetaDataPanelProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const dispatch = useDispatch();
  // Effective per-save format; saveMode/convertEmailToPDF are its wire
  // projections, consumed by the duplicate checks and save calls below.
  const { saveFormat, setSaveFormat, saveMode, convertEmailToPDF } = useSaveFormat();

  const files = useSelector((state: RootState) => !DragAndDropHelper.isDroppedModeActive ? state.files.files.filter(x => x.isSelected === true) : state.files.files.filter(x => x.isSelected === true && x.isFileSystemEntry && !x.isFolder));
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const clientTypes = useClientTypeContext(currentDirectory?.siteId);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
  const siteInfo = useSelector((state: RootState) => state.tenantInfo?.sites);
  const tenantReplaceFileNameWithGUID = useSelector((state: RootState) => state.tenantInfo?.replaceFileNameWithGUID);
  const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
  const canOverwrite = useSelector((state: RootState) => state.files.canOverwrite);
  const datasourceValues = useSelector((state: RootState) => state.datasource.values);
  const datasourceInitialLoad = useSelector((state: RootState) => state.datasource.initialLoad);
  const datasourceCallerId = useSelector((state: RootState) => state.datasource?.callerId);
  const datasourceItems = useSelector((state: RootState) => state.tenantInfo.datasources);
  const savingLocation = useSelector((state: RootState) => state.savingLocation);

  const initialLoad = React.useRef(true);

  // Latest fileOptions, readable from async continuations and same-tick writes.
  // Every panel write goes through commitFileOptions so the ref never lags a
  // write this panel made itself.
  const fileOptionsRef = React.useRef<FileOption[]>(fileOptions);
  fileOptionsRef.current = fileOptions;
  const commitFileOptions = (nextFileOptions: FileOption[]) => {
    fileOptionsRef.current = nextFileOptions;
    setFileOptions(nextFileOptions);
  };

  // The selection is held as a snapshot but always rendered from fileOptions, so an
  // edit is visible in the same render that commits it. A value lagging one render
  // behind makes React restore the old input value, which moves the caret to the end.
  const [selectedFileSnapshot, setSelectedFile] = React.useState<FileOption>();
  const selectedFile = React.useMemo(
    () => (selectedFileSnapshot && fileOptions?.find(x => x.key === selectedFileSnapshot.key)) ?? selectedFileSnapshot,
    [fileOptions, selectedFileSnapshot]
  );
  // Duplicate-check bookkeeping. A result is applied only when it is the newest check
  // started for that file and the name it checked is still the file's name.
  const latestCheckIdByKey = React.useRef(new Map<string | number, number>());
  const inFlightChecks = React.useRef(0);
  const pendingNameCheckRef = React.useRef<{ key: string | number }>(null);
  const isMounted = React.useRef(true);
  const checkFileForDuplicatesRef = React.useRef<(key: string | number) => Promise<void>>();
  React.useEffect(() => () => {
    isMounted.current = false;
    // Closing inside a Name edit's debounce must not skip its check: the store's
    // duplicate flag and name still gate a save made after the panel is gone.
    const pending = pendingNameCheckRef.current;
    pendingNameCheckRef.current = null;
    if (pending) {
      checkFileForDuplicatesRef.current?.(pending.key);
    }
  }, []);

  const [inputValues, setInputValues] = React.useState<SharePointInputData[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [savingButtonProps, setSavingButtonProps] = React.useState<ISaveButtonProps>();
  const [saveAllFilesDisabledTooltip, setSaveAllFilesDisabledTooltip] = React.useState("");
  const [saveFileDisabledTooltip, setSaveFileDisabledTooltip] = React.useState("");
  // A fresh object per Name edit, so every edit (re)schedules the debounced check,
  // including an edit back to a previously checked value.
  const [pendingNameCheck, setPendingNameCheck] = React.useState<{ key: string | number }>(null);
  const [verifyingFiles, setVerifyingFiles] = React.useState(false);
  const [fieldsLoaded, setFieldsLoaded] = React.useState(false);
  const [selectionOpen, setSelectionOpen] = React.useState(false);

  React.useEffect(() => {
    if (checkForDuplicates || (initialLoad.current && selectedFile)) {
      initialLoad.current = false;
      checkFileForDuplicates(selectedFile?.key);
    }
  }, [checkForDuplicates, selectedFile?.key])

  // The duplicate-check filename follows the chosen save format (X.pdf vs
  // X.<ext>): re-verify the selected file when the format changes, so its
  // isDuplicate/error state never reflects a stale outcome name — the
  // save-time re-check (checkPdfFiles) only covers SaveAsPdf. Files checked
  // earlier under another format refresh the same way when next selected.
  useDidMountEffect(() => {
    if (selectedFile) {
      checkFileForDuplicates(selectedFile.key);
    }
  }, [saveFormat]);

  React.useEffect(() => {
    if (showSelectVersion || spHasDuplicateFiles) {
      setSelectionOpen(true);
    } else {
      setSelectionOpen(false);
    }
  }, [showSelectVersion, spHasDuplicateFiles])

  React.useEffect(() => {
    generalModule.setIsMultiMetaFileUpdatePanelOpen(true);
    setSelectedCheckinType(CheckinTypes.minorCheckIn);
  }, []);

  React.useEffect(() => {
    if (datasourceValues) {
      updateMetadataPropertiesFromDataSource(datasourceValues);
    }
  }, [datasourceValues, selectedFile, fileOptions]);

  React.useEffect(() => {
    pendingNameCheckRef.current = pendingNameCheck;
    if (!pendingNameCheck) {
      return undefined;
    }

    setVerifyingFiles(true);
    const debounceTimer = setTimeout(() => {
      pendingNameCheckRef.current = null;
      checkFileForDuplicates(pendingNameCheck.key);
    }, 800);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [pendingNameCheck]);

  // The save button no longer carries the "as Pdf" suffix — the Save format
  // radio states the chosen format.
  React.useEffect(() => {
    setSavingButtonProps(prevVal => {
      return {
        ...prevVal,
        text: `${languageStrings.SaveTo} ${savingLocation.display}`
      };
    });
  }, [savingLocation]);

  const canConvertToPDF = React.useMemo(() => {
    return files.filter(f => f.isSelected).some(f => f.canSaveAsPDF);
  }, [savingButtonProps, files]);

  // Whether the selection contains an email message — without one, "Emails as
  // PDF" would equal "Keep original formats", so the option is hidden.
  const showEmailFormatOption = React.useMemo(() => {
    return files.some(f => f.isSelected && isEmailFormatItem(f));
  }, [files]);

  // Whether the selection contains a convertible non-email item — without one,
  // "Everything as PDF" would equal "Emails as PDF", so the option is hidden.
  const showEverythingFormatOption = React.useMemo(() => {
    return files.some(f => f.isSelected && f.canSaveAsPDF && !isEmailFormatItem(f));
  }, [files]);

  // The taller (3-option) footer space is reserved only when both optional
  // radio entries are visible at once.
  const showThreeFormatOptions = showEmailFormatOption && showEverythingFormatOption;

  const panelCssStyles = React.useMemo(() => {
    const partialStyles: Partial<IPanelStyles> = {
      main: {
        overflow: "hidden"
      },
      content: {
        // Reserve room for the footer: base button row, plus the Save-format
        // radio when convertible files are selected (taller with three options).
        height: canConvertToPDF
          ? (showThreeFormatOptions ? "calc(100vh - 255px)" : "calc(100vh - 225px)")
          : "calc(100vh - 140px)",
        paddingLeft: 5,
        paddingRight: 5,
        paddingBottom: 0
      },
      footer: {
        backgroundColor: "var(--db-bg, #fff)"
      },
    };
    return partialStyles;
  }, [canConvertToPDF, showThreeFormatOptions]);

  const updateMetadataPropertiesFromDataSource = (values: any) => {
    if (selectedFile && fileOptions?.length > 0) {
      const isMultipleValues: boolean = Array.isArray(values) && values.length > 0;
      const propertyKeys = isMultipleValues
        ? Object.keys(values[0])
        : Object.keys(values);

      if (propertyKeys.length > 0) {
        const selectedFileInputValues = fileOptions.find(x => x.key === selectedFile.key)?.fileInputValues;
        if (selectedFileInputValues) {
          const updatedinputValuesToSave = [...selectedFileInputValues];
          propertyKeys.forEach((propKey) => {
            let updatedValue: any;
            if (isMultipleValues) {
              const uniqueValues = values
                .map(value => value[propKey])
                .filter((value, index, self) => self.indexOf(value) === index);
              updatedValue = uniqueValues;
            }
            else {
              updatedValue = values[propKey];
            }
            updateDatasourceDisplayNameInputData(updatedinputValuesToSave, propKey, updatedValue, isMultipleValues);
          });
          updateSharepointMetadataToSave(updatedinputValuesToSave, null);
        }
      }
    }
    dispatch(setDatasourceValues(null));
  };

  const updateDatasourceDisplayNameInputData = (
    updatedinputValuesToSave: SharePointInputData[],
    propKey: string,
    value: any,
    isMultipleValues: boolean
  ) => {
    const selectedFileInputValues = fileOptions.find(x => x.key === selectedFile.key)?.fileInputValues;

    let inputDatasourceId: number;
    let inputToUpdate = selectedFileInputValues.find(
      (item) => {
        // item.displayName && item.displayName.trim().toLowerCase() === propKey.trim().toLowerCase()
        const boundName: string = item.displayName?.trim()?.toLowerCase();
        if (boundName && item.internalName !== sharePointInternalFileName && boundName === propKey.trim().toLowerCase()) {
          inputDatasourceId = datasourceCallerId && DatasourceHelper.getInputDataSource(datasourceItems, boundName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""], clientTypes)?.id;
          return true;
        }
        return false;
      }
    );

    if (inputToUpdate) {
      const inputDatasourceCompatible: boolean = Number.isInteger(inputDatasourceId) && inputDatasourceId === datasourceCallerId;
      if (value === refrehOptionsRequest) {
        const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
        if (updatedInput) {
          updatedInput.resetDatasourceOptionsToggle = !updatedInput.resetDatasourceOptionsToggle;
        }
      }
      else if (isMultipleValues || inputDatasourceCompatible) { //value param is an array
        try {
          const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
          if (updatedInput) {
            if (!Array.isArray(value)) {
              value = [value];
            }
            if (updatedInput.value) {
              if (Array.isArray(updatedInput.value) && updatedInput.value.length > 0) {
                updatedInput.value = updatedInput.value
                  .map(val => {
                    if (val && value.some(x => x?.trim().toLowerCase() === val.trim().toLowerCase())) {
                      return val;
                    }
                    return null;
                  })
                  .filter(val => val);
              }
              else if (!datasourceInitialLoad) {
                if (value.length === 1) {
                  DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
                }
                else if ((value.length > 1 && !value.some(x => x?.trim().toLowerCase() === updatedInput?.value?.trim().toLowerCase()))) {
                  updatedInput.value = null;
                }
              }
            }
            else if (value.length === 1) {
              DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
            }
            updatedInput.updatedDatasourceOptions = value;
          }
        } catch (error) {
          logger(error, "CMMultipleFilesMetaDataPanel.tsx (updateDatasourceDisplayNameInputData)");
        }
      }
      else {
        DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value);
      }
    }
  };

  const fieldsChanged = async (fields: any[], contentTypeId: string, inputValues?: SharePointInputData[]) => {
    if (fileOptions?.length === 0) {
      // Await the population, not just the cache: opening the per-file properties
      // can race the header load in OutlookService.getItems.
      const selectedItems = await getSelectedExchangeItemsAsync();

      const options = await Promise.all((files as IFileToUpload[])?.map(async (file, index) => {
        // Resolve purely by id: only email files (and their attachments) carry a
        // graphMailItemId, in both the content-manager copy-emails flow and the
        // Outlook multi-select flow, so non-email files always resolve to null.
        const selectedItem = selectedItems?.find(x => x.id === file.graphMailItemId) ?? null;

        return {
          key: file.id,
          text: file.name,
          file: file,
          isSelectedFile: index === 0,
          isModified: false,
          canOverwrite,
          isSaving: false,
          savingSuccess: false,
          savingError: false,
          fileInputValues: await Promise.all(inputValues.map(async inputValue => {
            let extention;
            let value = inputValue.value;
            if (inputValue.internalName === sharePointInternalFileName || inputValue.internalName === sharePointInternalTitleName) {
              value = file.name;
              if (inputValue.internalName === sharePointInternalFileName) {
                extention = file.extension;
              }
            } else {
              value = originalInputValues.find(x => x.internalName === inputValue.internalName)?.value;
            }

            value = resolveEmailMappedInputValue(inputValue, value, selectedItem);

            let hasError = inputValue.hasError;
            if (inputValue.internalName === sharePointInternalFileName) {
              hasError = false;
            }
            else if (inputValue.xml?.Required === "TRUE" && value) {
              hasError = false;
            }

            return {
              ...inputValue,
              hasError,
              value,
              extention
            }
          })),
        }
      }));

      setFileOptions(options);
      setSelectedFile((options as any[])[0]);
      setFieldsLoaded(true);
    }
    else {
      const selectedOption = fileOptions.find(x => x.isSelectedFile);
      setSelectedFile(selectedOption ? selectedOption : (fileOptions as any[])[0]);
      setFieldsLoaded(true);
    }
  }

  const onPanelCLose = () => {
    generalModule.setIsMultiMetaFileUpdatePanelOpen(false);
    closePanel();
  }

  const updateSharepointMetadataToSave = (metadata: SharePointInputData[], informationProtectionId: string, changedIndex?: number) => {
    try {
      const changedInput = changedIndex !== undefined ? metadata.find(x => x.index === changedIndex) : undefined;
      const nameChanged = !!selectedFile && changedInput?.internalName === sharePointInternalFileName;
      // The save reads option.file.name, so a typed Name is committed to the file here,
      // independent of whether its debounced duplicate check ever completes. An empty
      // Name keeps the last non-empty one (the save has never accepted an empty name).
      const typedName: string = nameChanged && typeof changedInput.value === "string" ? changedInput.value : "";

      const newFileOptions = fileOptionsRef.current.map(option => {
        if (selectedFile && selectedFile?.key === option.key) {
          return {
            ...option,
            ...(typedName && option.file ? { file: { ...option.file, name: typedName }, text: typedName } : {}),
            isModified: true,
            fileInputValues: metadata.map(inputValue => {
              let value = inputValue.value;

              return {
                ...inputValue,
                value
              }
            })
          }
        }

        return {
          ...option
        }
      });

      commitFileOptions(newFileOptions);

      if (nameChanged) {
        const nextPendingNameCheck = { key: selectedFile.key };
        // Set synchronously so a check settling before the effect runs keeps "verifying" on.
        pendingNameCheckRef.current = nextPendingNameCheck;
        setPendingNameCheck(nextPendingNameCheck);
      }
    } catch (error) {
      logger(error, "CMMultipleFilesMetaDataPanel.tsx (updateSharepointMetadataToSave)");
    }
  };

  // The name a file will be saved under: the Name field when present, else the file name.
  const getOptionName = (option: FileOption): string => {
    const nameInput = option?.fileInputValues?.find(x => x.internalName === sharePointInternalFileName);
    return nameInput ? nameInput.value : option?.file?.name;
  };

  const finishCheck = () => {
    if (!isMounted.current) {
      return;
    }
    setCheckForDuplicates(false);
    if (inFlightChecks.current === 0 && !pendingNameCheckRef.current) {
      setVerifyingFiles(false);
    }
  };

  // Checks one file (by key) under the name it currently has. The target is fixed
  // before the await: the selection may change while the request is in flight.
  const checkFileForDuplicates = async (key: string | number) => {
    const target = fileOptionsRef.current?.find(x => x.key === key);
    const nameToCheck = getOptionName(target);
    if (!target?.file || !nameToCheck) {
      finishCheck();
      return;
    }

    const checkId = (latestCheckIdByKey.current.get(key) ?? 0) + 1;
    latestCheckIdByKey.current.set(key, checkId);
    inFlightChecks.current++;

    try {
      const file: IFileToUpload = { ...target.file, name: nameToCheck };

      let preCommitResult: IFilePreCommitResult;
      if (!file.isFolder && !file.parentFolderId) {

        const dest = await resolveDestinationUrl(currentDirectory);

        preCommitResult = await getFilePreCommitInfo({
          accessToken: await authModule.getSharePointAccessToken(),
          destinationUrl: dest,
          siteUrl: currentDirectory.webUrl,
          fileName: willSaveAsPdf(file, convertEmailToPDF, saveMode) ? `${file.name}.pdf` : `${file.name}.${file.extension}`,
          autoFolderCreation: !(new FolderView(siteInfo, currentDirectory).canViewFolder())
        });
      }

      if (!preCommitResult) {
        preCommitResult = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null };
      }

      // Drop a result that no longer describes the file: a newer check for it was
      // started (another edit, a save-format change, a re-selection), the option is
      // gone, or the file was renamed since (that rename queued its own check).
      const current = fileOptionsRef.current?.find(x => x.key === key);
      if (latestCheckIdByKey.current.get(key) !== checkId
        || !current?.file
        || getOptionName(current) !== nameToCheck) {
        return;
      }

      const checkResult = {
        name: nameToCheck,
        isDuplicate: preCommitResult.exists,
        filePreCommitResult: preCommitResult
      };

      // The store's duplicate flag gates the save, so it is written even if the panel
      // closed meanwhile; the reducer merges it into the live entry.
      dispatch(updateFileCheckResult({ id: current.file.id, ...checkResult }));

      // After close, fileOptions belong to the parent alone; this panel's view is stale.
      if (!isMounted.current) {
        return;
      }

      commitFileOptions(fileOptionsRef.current.map(option => {
        if (option.key === key) {
          return {
            ...option,
            fileInputValues: option.fileInputValues.map(inputValue => {
              return {
                ...inputValue,
                hasError: (inputValue.internalName === sharePointInternalFileName && preCommitResult.exists) ? true : inputValue.hasError,
                errorMessage: (inputValue.internalName === sharePointInternalFileName && preCommitResult.exists) ? languageStrings.FileSameName : inputValue.errorMessage,
              }
            }),
            file: { ...option.file, ...checkResult },
            name: nameToCheck,
            text: nameToCheck
          }
        }

        return {
          ...option
        }
      }));
    }
    catch (error) {
      logger(error, "CMMultipleFilesMetaDataPanel.tsx (checkFileNamesForDuplicates)");
    }
    finally {
      inFlightChecks.current--;
      finishCheck();
    }
  }

  checkFileForDuplicatesRef.current = checkFileForDuplicates;

  const onFileChange = (event: React.FormEvent<HTMLDivElement>, selectedOption?: FileOption, index?: number) => {
    // A Name edit still inside its debounce belongs to the file being left: check it
    // now rather than letting the re-selection cancel it.
    const pending = pendingNameCheckRef.current;
    if (pending && pending.key !== selectedOption.key) {
      checkFileForDuplicates(pending.key);
    }

    const newFileOptions = fileOptionsRef.current.map(option => {
      return {
        ...option,
        isSelectedFile: selectedOption.key === option.key ? true : false,
      }
    })
    commitFileOptions(newFileOptions);

    const option = newFileOptions.find(x => x.key === selectedOption.key);
    setSelectedFile(option);
    // The newly selected file is re-verified through the same debounce as a Name edit.
    const nextPendingNameCheck = { key: option.key };
    pendingNameCheckRef.current = nextPendingNameCheck;
    setPendingNameCheck(nextPendingNameCheck);
  }

  const setSelectedIndividualFile = (selectedIndividualFile: FileOption) => {
    setSelectedFile(selectedIndividualFile);

    commitFileOptions(fileOptionsRef.current.map(fileOption => {
      if (fileOption.key === selectedIndividualFile.key) {
        return {
          ...selectedIndividualFile,
          isModified: true
        }
      }

      return { ...fileOption }
    }));
  }

  const isSaveAllFilesButtonDisabled = React.useMemo(() => {
    if (verifyingFiles || isBusySaving || selectedFile?.isSaving) {
      return true;
    }

    if (currentSharepointFolder.userPermissions?.canEditListItem === false) {
      setSaveAllFilesDisabledTooltip(languageStrings.WrongUserPermissionsUpdate);
      return true;
    }

    if (selectedFile?.file?.isDuplicate && !selectedFile?.canOverwrite) {
      setSaveAllFilesDisabledTooltip(languageStrings.OverwriteRequired);
      return true;
    }

    let isDisabled = false;
    fileOptions.forEach(fileOption => {
      if (!isDisabled && fileOption?.file?.isDuplicate && !fileOption?.canOverwrite) {
        isDisabled = true;
      }
    });

    setSaveAllFilesDisabledTooltip(isDisabled ? languageStrings.OverwriteRequired : "");
    return isDisabled;
  }, [currentSharepointFolder, selectedFile, fileOptions, verifyingFiles, isBusySaving]);

  const isSaveFileButtonDisabled = React.useMemo(() => {
    if (verifyingFiles || isBusySaving) {
      return true;
    }

    if (currentSharepointFolder.userPermissions?.canEditListItem === false) {
      setSaveFileDisabledTooltip(languageStrings.WrongUserPermissionsUpdate);
      return true;
    }

    setSaveFileDisabledTooltip("");
    return false;
  }, [currentSharepointFolder, selectedFile, verifyingFiles, isBusySaving])

  // Preview of the stored outcome, matching the taskpane document list:
  // "pdf" when the chosen save format converts the file, else its real extension.
  const getOutcomeExtension = (file: IFileToUpload) =>
    willSaveAsPdf(file, convertEmailToPDF, saveMode) ? "pdf" : file?.extension;

  const getFileDisplayName = (file: IFileToUpload) => {
    const extension = getOutcomeExtension(file);
    return extension ? `${file.name}.${extension}` : file.name;
  };

  const onRenderOption = (option: FileOption): JSX.Element => {
    const displayName = getFileDisplayName(option.file);
    return (
      <Stack horizontal verticalAlign="center" tokens={stackTokens} styles={fileOptionStackStyles}>
        <Icon
          {...getFileTypeIconProps({
            extension: getOutcomeExtension(option.file),
            size: 20,
            imageFileType: "svg",
          })}
          style={fileTypeIconStyles}
        />
        <TooltipHost content={displayName} styles={fileNameStyles} overflowMode={TooltipOverflowMode.Self}>
          <span>{displayName}</span>
        </TooltipHost>
      </Stack>
    );
  };

  const onRenderTitle = (options: FileOption[]): JSX.Element => {
    const option = options?.length > 0 && options[0];
    const displayName = option ? getFileDisplayName(option.file) : "";
    return (
      <Stack horizontal verticalAlign="center" tokens={stackTokens}>
        <Icon
          {...getFileTypeIconProps({
            extension: option ? getOutcomeExtension(option.file) : undefined,
            size: 20,
            imageFileType: "svg",
          })}
          style={fileTypeIconStyles}
        />
        <TooltipHost content={displayName} styles={fileNameStyles} overflowMode={TooltipOverflowMode.Self}>
          <span>{displayName}</span>
        </TooltipHost>
      </Stack>
    );
  };

  const onRenderFooterContent = React.useCallback(
    () => (
      <Stack>
        {canConvertToPDF
          && <SaveFormatChoice
            saveFormat={saveFormat}
            setSaveFormat={setSaveFormat}
            showEmailOption={showEmailFormatOption}
            showEverythingOption={showEverythingFormatOption}
          />}
        <TooltipHost content={saveAllFilesDisabledTooltip} calloutProps={tooltipHostCalloutProps}>
          <PrimaryButton
            className={!generalModule.isInConentMangerMode() ? (getPlatform == Office.PlatformType.iOS ? "xb-button-save ios-clean-button" : "xb-button-save") : "xb-button-save"}
            disabled={isSaveAllFilesButtonDisabled}
            iconProps={{ iconName: 'SaveAll' }}
            onClick={() => {
              saveFiles(saveMode, undefined, selectionOpen, files.some(x => x.parentFolderId));
              setSelectionOpen(false);
            }}
            text={verifyingFiles ? languageStrings.VerifyingFiles : savingButtonProps?.text}
          >
            {isBusy && <Spinner size={SpinnerSize.small} />}
          </PrimaryButton>
        </TooltipHost>
      </Stack>
    ),
    [saveAllFilesDisabledTooltip, isSaveAllFilesButtonDisabled, verifyingFiles, saveFileDisabledTooltip, isSaveFileButtonDisabled, selectedFile, isBusy, selectionOpen, saveMode, saveFormat, savingButtonProps, files, showEmailFormatOption, showEverythingFormatOption],
  );

  return (
    <Panel
      headerText={languageStrings.UpdateIndividualFile}
      isOpen={panelIsOpen}
      onDismiss={() => onPanelCLose()}
      isLightDismiss={false}
      closeButtonAriaLabel="Close"
      type={generalModule.isInConentMangerMode() ? PanelType.custom : PanelType.smallFluid}
      customWidth={generalModule.isInConentMangerMode() ? '350px' : undefined}
      styles={panelCssStyles}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom={true}
    >
      {selectionOpen && <div className="xb-content-lite">
        {spHasDuplicateFiles && <DuplicateFiles showSelectVersion={showSelectVersion} onCancel={() => { onCancel(selectedFile); setSelectionOpen(false); }} />}
        {showSelectVersion && <VersionSelectorMinMajOnly checkinTypeChanged={setSelectedCheckinType} onCancel={() => { onCancel(selectedFile); setSelectionOpen(false); }} labelFontSize={16} paddingTop={0} paddingLeft={0} />}
      </div>}
      <Stack tokens={containerStackTokens} styles={containerStackStyles} style={{ display: selectionOpen ? "none" : "" }}>
        <Stack.Item>
          {selectedFile?.isSaving === true &&
            <MessageBar>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <Stack.Item>{`${languageStrings.SavingFiles}...`}</Stack.Item>
                <Stack.Item><Spinner size={SpinnerSize.small} /></Stack.Item>
              </Stack>
            </MessageBar>}
          {selectedFile?.savingSuccess === true && <MessageBar messageBarType={MessageBarType.success}>{languageStrings.FileSaveSuccessSingle}</MessageBar>}
          {selectedFile?.savingError === true && <MessageBar messageBarType={MessageBarType.error}>{languageStrings.ErrorMessageGeneric}</MessageBar>}
        </Stack.Item>
        <Stack.Item>
          <div style={dropdownStyles}>
            <Dropdown
              placeholder={languageStrings.SelectAFile}
              label={languageStrings.SelectedFile}
              options={fileOptions}
              onChange={onFileChange}
              selectedKey={selectedFile?.key}
              // responsiveMode={ResponsiveMode.small}
              onRenderOption={onRenderOption}
              onRenderTitle={onRenderTitle}
            />
            <Separator />
          </div>
        </Stack.Item>
        <Stack.Item grow styles={metadataStackItemStyles}>
          <div className="xb-content">
            {/* The FileName entry's extension is snapshotted when the fields are
              built; re-project it on every render so the Name suffix previews the
              live save format without touching the per-file edit state. */}
            <SaveWithMetaData
              isInfoMode={true}
              inputValuesToSave={selectedFile
                ? selectedFile.fileInputValues.map(inputValue =>
                  inputValue.internalName === sharePointInternalFileName && inputValue.extention
                    ? { ...inputValue, extention: getOutcomeExtension(selectedFile.file) }
                    : inputValue)
                : inputValues}
              updateSharepointMetadataToSave={updateSharepointMetadataToSave}
              fieldsChanged={fieldsChanged}
              addNameField={!tenantReplaceFileNameWithGUID}
              selectedIndividualFile={selectedFile}
              setSelectedIndividualFile={setSelectedIndividualFile}
            ></SaveWithMetaData>
          </div>
        </Stack.Item>
      </Stack>
      <ToastContainer autoClose={3000} hideProgressBar transition={Slide}></ToastContainer>
    </Panel>
  )
}
